import {Indicator, Node, Paragraph, SequenceElementType} from "@/types/models";
import {createContext, Dispatch, PropsWithChildren, useContext, useEffect, useReducer, useRef, useState} from "react";
import {useSessionMetadataContext} from "@/Context/SessionMetadataContext";
import {flattenHierarchy, getChildren} from "@/services/NodeService";
import {getSurveyNodeContent} from "@/services/SurveyService";
import {NodeAction, nodeReducer} from "@/Reducers/NodeReducer";
import {indexChar, toRoman} from "@/utils/strings";
import {pushUrl} from "@/utils/history";
import {useProgressContext} from "@/Context/ProgressContext";
import {readSessionLayout, storeSessionLayout} from "@/utils/sessionLayout";

type NodeNavigationContextProps = {
    activeNode: Node | undefined
    setActiveNode: Dispatch<Node | undefined>
    activateNode: (node: Node, keepNav?: boolean) => void
    nodes: Node[]
    setNodes: Dispatch<NodeAction>
    nextNode: Node | undefined
    previousNode: Node | undefined
    fetchChildren: (node: Node, setChildren?: (children: Node[]) => void) => Promise<Node[] | undefined>
    fetchNodeContent: (node: Node, nodeIndex?: number) => Promise<Node | undefined>
    getRouteParams: (node: Node, nodeIndex?: number | undefined) => {session?: number, node?: number, index?: number}
    isReady: () => boolean
    showingNav: boolean
    setShowingNav: (showing: boolean) => void
    pinnedNav: boolean
    setPinnedNav: (pinned: boolean) => void
    findNode: (idOrHandle: number | string, arrayIndex?: number, includeInline?: boolean, searchIn?: Node[], parentArrayIndex?: string | number | null) => Node | undefined
    findIndicator: (idOrHandle: number | string, node?: Node) => Indicator | undefined
    findSequenceElementType: (id: number) => SequenceElementType | undefined
    getAscendants: (node: Node) => Node[]
    action: string | undefined
    setAction: Dispatch<string | undefined>
    lastUpdate: number
    updateChildren: (node: Node, index?: number) => void
    linkChildren: (node: Node, children?: Node[], skipAddToArray?: boolean) => Node[]
    updateNodesText: (flatNodes: Node[]) => void
    checkCanMove: (nodes: Node[], activeArrayNode?: Node) => void
}

const NodeNavigationContext = createContext<NodeNavigationContextProps | undefined>(undefined)

export const NodeNavigationContextProvider = ({children}: PropsWithChildren) => {
    const {
        session,
        survey,
        rootNode,
        setRootNode,
        initialNode,
        nodeTypes,
        sequenceTypes,
        mode
    } = useSessionMetadataContext()
    const {setLoading, setForceShowActiveNode} = useProgressContext()

    const [activeNode, setActiveNode] = useState<Node | undefined>(undefined)
    const [nodes, setNodes] = useReducer(nodeReducer, [])
    const [nextNode, setNextNode] = useState<Node | undefined>(undefined)
    const [previousNode, setPreviousNode] = useState<Node | undefined>(undefined)
    const [showingNav, setShowingNav] = useState(false)
    const [pinnedNav, setPinnedNavState] = useState<boolean>(() => readSessionLayout('pinnedNav') ?? false)

    /**
     * The reader's own choice, so it is kept for the next visit on this device.
     */
    const setPinnedNav = (pinned: boolean) => {
        setPinnedNavState(pinned)
        storeSessionLayout('pinnedNav', pinned)
    }
    const [action, setAction] = useState<string | undefined>(undefined)
    const [lastUpdate, setLastUpdate] = useState(0)

    const processingInitialNode = useRef(false)

    // The node the page was opened at, once the reader has been shown it, and the survey shown from its overview.
    const initialNodeShown = useRef<Node | undefined>(undefined)
    const overviewShownFor = useRef<number | undefined>(undefined)
    const isReady = () => !processingInitialNode.current

    const findNode = (idOrHandle: number | string, arrayIndex?: number, includeInline: boolean = true, searchIn?: Node[], parentArrayIndex?: string | number | null): Node | undefined => {
        const searchNodes = searchIn ?? nodes
        if (!searchNodes || searchNodes.length === 0) return undefined;

        // Helper function to search within a single node
        const searchInNode = (node: Node): Node | undefined => {
            // Check if this node matches our criteria
            const matches = (candidate: Node): boolean => {
                // Include inline nodes if includeInline is true (default), exclude them if false
                if (!includeInline && candidate.inline) return false;

                // Match the ID or handle
                const idMatches = typeof idOrHandle === 'number'
                    ? candidate.id === idOrHandle
                    : candidate.handle === idOrHandle;

                if (!idMatches) return false;

                // A repeated node is identified by more than its own index: a node that does not itself
                // repeat sits at array_index 0 inside a repeated ancestor, and which repetition it belongs to
                // is carried on parent_array_index. Compared as strings because the tree builds that value by
                // interpolation, and nested repeats make it a dotted path.
                if (parentArrayIndex !== undefined && parentArrayIndex !== null
                    && String(candidate.parent_array_index ?? '') !== String(parentArrayIndex)) {
                    return false;
                }

                // If arrayIndex is specified, match it exactly
                if (arrayIndex !== undefined) {
                    return candidate.array_index === arrayIndex;
                }

                return true;
            };

            // Check if the current node matches
            if (matches(node)) {
                return node;
            }

            // If we're including inline nodes, search through this node's content
            if (includeInline && node.content) {
                for (const contentItem of node.content) {
                    // Content can include indicators, paragraphs, and inline nodes
                    if (contentItem._class === 'Node' && matches(contentItem as Node)) {
                        return contentItem as Node;
                    }
                }
            }

            // Search through direct children in the hierarchy
            if (node.children) {
                for (const child of node.children) {
                    const found = searchInNode(child);
                    if (found) return found;
                }
            }

            return undefined;
        };

        // Start search from all top-level nodes
        for (const node of searchNodes) {
            const found = searchInNode(node);
            if (found) return found;
        }

        // Fallback: if we didn't find with exact array_index matching and no arrayIndex was specified,
        // try to find the base node (without array_index) as fallback
        if (arrayIndex === undefined) {
            for (const node of searchNodes) {
                const searchForBase = (candidate: Node): Node | undefined => {
                    if (!includeInline && candidate.inline) return undefined;

                    const idMatches = typeof idOrHandle === 'number'
                        ? candidate.id === idOrHandle
                        : candidate.handle === idOrHandle;

                    if (idMatches && (candidate.array_index === undefined || candidate.array_index === null)) {
                        return candidate;
                    }

                    // Search in content for inline nodes
                    if (includeInline && candidate.content) {
                        for (const contentItem of candidate.content) {
                            if (contentItem._class === 'Node') {
                                const found = searchForBase(contentItem as Node);
                                if (found) return found;
                            }
                        }
                    }

                    // Search in children
                    if (candidate.children) {
                        for (const child of candidate.children) {
                            const found = searchForBase(child);
                            if (found) return found;
                        }
                    }

                    return undefined;
                };

                const found = searchForBase(node);
                if (found) return found;
            }
        }

        return undefined;
    };

    const findIndicator = (idOrHandle: number | string, node?: Node): Indicator | undefined => {
        if (!node) node = rootNode!
        let indicator = typeof idOrHandle === 'number'
            ? node?.content?.find(i => i._class === 'Indicator' && i.id === idOrHandle || i.handle === `${idOrHandle}`)
            : node?.content?.find(i => i._class === 'Indicator' && i.handle === idOrHandle)

        if (!indicator) {
            const inlineNodes = node.content?.filter(c => c._class === 'Node') ?? []
            for (const inlineNode of inlineNodes) {
                indicator = findIndicator(idOrHandle, inlineNode)
                if (indicator) break;
            }
        }

        if (!indicator && node.children) {
            for (const child of node.children) {
                indicator = findIndicator(idOrHandle, child)
                if (indicator) break;
            }
        }

        return indicator as Indicator | undefined
    }

    const findSequenceElementType = (id: number): SequenceElementType | undefined => {
        if (!sequenceTypes) return undefined

        for (const sequenceType of sequenceTypes) {
            const elementType = sequenceType.element_types?.find(t => t.id === id)
            if (elementType) return elementType
        }

        return undefined
    }

    const getAscendants = (node: Node): Node[] => {
        const ascendants: Node[] = []
        while (node.parent) {
            node = node.parent
            ascendants.push(node)
        }

        return ascendants
    }

    const fetchChildren = async (node: Node, setChildren?: (children: Node[]) => void) => {
        node.children = []
        setLoading(true)
        const promise: Promise<Node[]> = getChildren(node, session);
        promise.then((children: Node[]) => {
            handleChildrenUpdate(node, children, setChildren)
            setLoading(false)
        })

        return promise
    }

    const fetchNodeContent = async (node: Node, nodeIndex?: number | undefined) => {
        if (node.content !== undefined) return
        setLoading(true)
        const promise: Promise<Node> = getSurveyNodeContent(session!, node, nodeIndex)
        promise.then((nodeWithContent: Node) => {
            node.content = nodeWithContent.content
            node.type = node.type || nodeTypes?.find(t => t.id === node.node_type_id)
            setNodes({type: "SET", nodes: [...nodes]})
            setLoading(false)
        })

        return promise
    }

    const getRouteParams = (node: Node, arrayIndex?: number | undefined) => {
        const params: {session?: number, node?: number, index?: number} = {session: session?.id, node: node?.id}
        if (arrayIndex) params.index = arrayIndex

        return params
    }

    const activateNode = (node: Node, keepNav?: boolean) => {
        const found = nodes.find(
            n =>
                n.id === node.id &&
                n.array_index === node.array_index &&
                n.parent_array_index === node.parent_array_index
        )

        setActiveNode(found ?? node)

        if (!keepNav && !pinnedNav) setShowingNav(false)
    }

    const handleChildrenUpdate = (node: Node, children: Node[], setChildren?: (children: Node[]) => void, newChildren?: Node[]) => {
        if (!newChildren) newChildren = linkChildren(node, children)
        const allNodes = [...nodes, ...newChildren]
        setNodes({type: "SET", nodes: allNodes})
        checkCanMove(allNodes)
        if (setChildren) setChildren(node.children!)
    }

    const updateChildren = (node: Node, index: number = 0) => {
        if (!node.inline) {
            const x = nodes.findIndex(n => n.id === node.id)
            if (x === -1)
                setNodes({type: "SET", nodes: [...nodes, node]})
            else
                setNodes({type: "SET", nodes: [...nodes.slice(0, x), node, ...nodes.slice(x + 1)]})
        }
        node.type = node.type || nodeTypes?.find(t => t.id === node.node_type_id)
        node.index = index
        if (index) {
            if (node.inline) {
                const indexString = node.parent && node.parent.inline ? toRoman(index).toLowerCase() : indexChar(index)
                node.prefix = node.parent && node.parent.prefix ? `${node.parent.prefix}${indexString}.` : `${indexString}.`
            } else {
                node.prefix = node.parent && node.parent.prefix ? `${node.parent.prefix}${index}.` : `${index}.`
            }
        }

        const inlineNodes = node.content?.filter(c => c._class === 'Node') ?? []
        let i = 0
        inlineNodes.forEach((c, i) => {
            c.parent = node
            updateChildren(c, ++i)
        })

        if (!node.children) return

        node.children.forEach(c => {
            c.parent = node
            c.child_index = i
            updateChildren(c, ++i)
        })
    }

    const linkChildren = (node: Node, children?: Node[], skipAddToArray?: boolean) => {
        const newChildren = []

        if (node.children === undefined) node.children = []
        if (children === undefined) children = node.children
        let childIndex: number = 0
        for (const child of children) {
            child.parent = node
            child.child_index = childIndex++

            child.xlft = child.lft
            child.xrgt = child.rgt
            child.xtitle = child.title
            child.xname = child.name
            child.xdescription = child.description

            if (!node.children.find(c => c.id === child.id)) {
                node.children.push(child)
                newChildren.push(child)
            }
            if (child.array_max && !skipAddToArray) {
                if (child.array_count) {
                    child.array_index = 1
                    for (let i = 2; i <= child.array_count; i++) {
                        const sibling = includeArrayNode(child)
                        if (sibling) childIndex++
                    }
                } else {
                    child.array_index = 0
                }
            }

            fixNodeText(child)
        }
        node.children_aware = true
        if (node === survey!.rootNode) setRootNode({...node})

        updateTreeEdges()

        return newChildren
    }

    const findArrayIndex = (node: Node): number | undefined => {
        let current: Node | undefined = node
        while (current && current.array_index === undefined) {
            current = current.parent
        }

        return current?.array_index
    }

    const includeArrayNode = (node: Node): Node | null => {
        if (!node.array_max) {
            console.warn("Can't add array node without array_max.", node)
            return null
        }

        const parent = node.parent
        if (!parent) {
            console.warn("Can't add array node without parent.", node)
            return null
        }

        const siblings = parent.children?.filter(n => n.id === node.id) ?? []
        if (siblings.length >= node.array_max) {
            console.warn("Can't add array node because array_max reached.", node)
            return null
        }

        let index: number = node.array_index ?? 0
        if (index === 0) {
            node.array_index = 1
            fixNodeText(node)

            return node
        }

        const sibling = {
            ...node, array_index: siblings.length + 1, child_index: parent!.children!.length + 1
        }
        fixNodeText(sibling)

        parent.children!.push(sibling)

        return sibling
    }

    const updateTreeEdges = () => {
        const updateNodeEdges = (node: Node, i: number): number => {
            node.lft = ++i
            if (node.children) for (const child of node.children) i = updateNodeEdges(child, i)
            node.rgt = ++i

            return i
        }

        if (survey?.rootNode) updateNodeEdges(survey.rootNode, 0)
    }

    const fixNodeText = (node: Node | null): void => {
        if (!node) return

        let index = node.array_index
        if (index === undefined) index = findArrayIndex(node)
        if (!index) return

        const idx = `${index}`
        node.title = (node.xtitle ?? node.title).replace("~array_index~", idx)
        node.name = (node.xname ?? node.name).replace("~array_index~", idx)
        if (node.description) node.description = (node.xdescription ?? node.description).replace("~array_index~", idx)
    }

    const checkCanMove = (nodes: Node[], activeArrayNode?: Node) => {
        if (!activeNode) {
            setNextNode(survey?.rootNode)
            setPreviousNode(undefined)
            return
        }

        const flatNodes = flattenHierarchy(nodes)
        const getArrayNode = (node: Node): Node | undefined => {
            if (node.array_index) return findNode(node.id, node.array_index)

            while (node.parent) {
                if (node.parent.array_index) {
                    node.parent = findNode(node.parent_id!, node.parent.array_index)
                    return node.parent
                }
                node = node.parent
            }

            return undefined
        }

        /*
         * Where the active node is among them. An id is not enough: every repetition of a repeated section holds
         * the same nodes, so the id alone finds the first repetition's, and Next from the third sector led into
         * the first. The node itself is looked for first, then one of the same repetition, which a copy of it
         * still is, and only then the id.
         */
        const sameRepetition = (n: Node) => n.id === activeNode.id
            && (n.array_index ?? 0) === (activeNode.array_index ?? 0)
            && String(n.parent_array_index ?? '') === String(activeNode.parent_array_index ?? '')

        let activeNodeIndex = flatNodes.indexOf(activeNode)
        if (activeNodeIndex === -1) activeNodeIndex = flatNodes.findIndex(sameRepetition)
        if (activeNodeIndex === -1) activeNodeIndex = flatNodes.findIndex(
            activeArrayNode
                ? n => n.id === activeNode.id && getArrayNode(n) === activeArrayNode
                : n => n.id === activeNode.id
        )

        const previousNode = activeNodeIndex > 0 ? flatNodes[activeNodeIndex - 1] : undefined
        const nextNode = activeNodeIndex < flatNodes.length - 1 ? flatNodes[activeNodeIndex + 1] : undefined

        updateNodesText(flatNodes)

        setPreviousNode(previousNode)
        setNextNode(nextNode)
    }

    const updateNodesText = (flatNodes: Node[]) => {
        const fixText = (template: string, node: Node) => {
            let x = 0
            let i = template.indexOf("~")
            let fixed: string = ""
            while (i !== -1) {
                const end = template.indexOf("~", i + 1)
                if (end === -1) {
                    fixed += template.substring(x)
                    break;
                }

                fixed += template.substring(x, i)
                const chk = template.indexOf("|", i + 1)
                const key = template.substring(i + 1, chk !== -1 ? chk : end)
                const other = chk > -1 ? template.substring(chk + 1, end) : undefined
                if (key === "array_index") {
                    fixed += node.array_index ?? 0
                } else if (key === "node_index") {
                    fixed += node.child_index ?? 0
                } else if (key === "node_id") {
                    fixed += node.id
                } else {
                    const indicator = findIndicator(key, node)
                    if (indicator && indicator.response && (indicator.response.input || indicator.response.input_text)) {
                        fixed += indicator.response.input ?? indicator.response.input_text
                    } else {
                        fixed += other ?? template.substring(i, end)
                    }
                }

                x = end + 1
                i = template.indexOf("~", x)
            }
            if (x < template.length) fixed += template.substring(x)

            return fixed
        }
        for (const node of flatNodes) {
            if (node.metadata?.suffix) {
                node.suffix = fixText(`${node.metadata.suffix}`, node)
                node.processedTitle = `${node.title} ${node.suffix}`
            }
        }

        setLastUpdate(Date.now())
    }

    useEffect(() => {
        checkCanMove(nodes)
        setForceShowActiveNode(false)

        if (activeNode) {
            const getArrayNode = (node: Node): Node | undefined => {
                if (node.array_index) return findNode(node.id, node.array_index)

                while (node.parent) {
                    if (node.parent.array_index) {
                        node.parent = findNode(node.parent_id!, node.parent.array_index)
                        return node.parent
                    }
                    node = node.parent
                }

                return undefined
            }
            const arrayNode = getArrayNode(activeNode)
            const url = route(`survey.session.${mode}`, getRouteParams(activeNode, arrayNode?.array_index ?? undefined))
            if (window.location.pathname !== url) pushUrl(url)
        }
    }, [activeNode, pinnedNav])

    useEffect(() => {
        if (rootNode) updateChildren(rootNode)

        // Only set activeNode if we have valid data and not in upload mode
        if (mode !== 'upload') {
            /*
             * The page opens at the node its address names. That is done once: the root node is replaced whenever
             * the tree changes, when a repeated section is added or removed for one, and each time this used to
             * take the reader back to wherever the page had been opened, whatever they had moved to since.
             */
            if (initialNode && initialNodeShown.current === initialNode) {
                // Already shown; the reader is wherever they have gone from there.
            } else if (initialNode) {
                // Search the current rootNode tree directly rather than via the `nodes`
                // state: `nodes` is (re)populated from rootNode in a separate effect that
                // runs after this one, so it always lags a render behind a fresh/updated
                // rootNode. Searching it here would never find a just-loaded initialNode
                // and would incorrectly fall through to rootNode below.
                const found = rootNode
                    ? findNode(initialNode.id, initialNode.array_index, true, [rootNode], initialNode.parent_array_index)
                    : undefined
                if (found) {
                    setActiveNode(found)
                    initialNodeShown.current = initialNode
                } else {
                    // If initialNode specified but not found, fall back to rootNode
                    setActiveNode(rootNode)
                }
            } else if (rootNode) {
                if (overviewShownFor.current !== rootNode.id) setActiveNode(rootNode)
                overviewShownFor.current = rootNode.id
            } else {
                // Clear activeNode if no valid node available
                setActiveNode(undefined)
            }
        }
    }, [rootNode, initialNode])

    useEffect(() => {
        if (survey) setRootNode(survey.rootNode)
    }, [survey])

    useEffect(() => {
        // Whichever node is active by the time this is applied, not the one that was when the nodes changed: adding
        // a repeated section changes the nodes and then moves the reader into it, and read the other way this put
        // them straight back where they had been.
        setActiveNode(active => {
            if (!active) return active
            return nodes.find(n => n.id === active.id && n.array_index === active.array_index && n.parent_array_index === active.parent_array_index) ?? active
        })
    }, [nodes])

    useEffect(() => {
        if (rootNode) setNodes({type: "SET", nodes: flattenHierarchy([rootNode])})
        // The survey says how it starts; a reader who has chosen otherwise on this device keeps their choice.
        setPinnedNavState(readSessionLayout('pinnedNav') ?? !!survey?.metadata?.navigator)
    }, [rootNode])

    return <NodeNavigationContext.Provider value={{
        activeNode,
        setActiveNode,
        activateNode,
        nodes,
        setNodes,
        nextNode,
        previousNode,
        fetchChildren,
        fetchNodeContent,
        getRouteParams,
        isReady,
        showingNav,
        setShowingNav,
        pinnedNav,
        setPinnedNav,
        findNode,
        findIndicator,
        findSequenceElementType,
        getAscendants,
        action,
        setAction,
        lastUpdate,
        updateChildren,
        linkChildren,
        updateNodesText,
        checkCanMove,
    }}>
        {children}
    </NodeNavigationContext.Provider>
}

export function useNodeNavigationContext(): NodeNavigationContextProps {
    const context = useContext(NodeNavigationContext);
    if (!context) throw new Error("No NodeNavigationContext is defined")
    return context
}
